import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { Icon } from '@iconify/react';
import CountUp from 'react-countup';
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

const AnimatedFeatureCards = () => {
  // Refs for each card to detect when they're in view
  const progressRef = useRef(null);
  const notificationRef = useRef(null);
  const dataRef = useRef(null);
  
  // For swiper active slide tracking
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Check if each card is in view
  const progressInView = useInView(progressRef, { once: false, amount: 0.5 });
  const notificationInView = useInView(notificationRef, { once: false, amount: 0.5 });
  const dataInView = useInView(dataRef, { once: false, amount: 0.5 });
  
  // Card data array for easier iteration
  const cards = [
    {
      ref: progressRef,
      inView: progressInView,
      title: "Weekly progress summaries & performance insights",
      description: "Track learning progress with detailed analytics and personalized insights",
      renderContent: (isMobile) => (
        <div className="flex flex-col md:flex-row items-center gap-6 w-full mb-6  mt-auto">
          {/* Circular progress indicator */}
          <div className="relative w-32 h-32 flex-shrink-0">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              <circle 
                cx="50" 
                cy="50" 
                r="45" 
                fill="none" 
                stroke="#E5E7EB" 
                strokeWidth="8" 
              />
              
              {isMobile ? (
                // For mobile: Always animate regardless of viewport
                <motion.circle 
                  cx="50" 
                  cy="50" 
                  r="45" 
                  fill="none" 
                  stroke="#AE02FD" 
                  strokeWidth="8" 
                  strokeLinecap="round"
                  strokeDasharray="283"
                  initial={{ strokeDashoffset: 283 }}
                  animate={{ strokeDashoffset: 283 - (283 * 0.9) }}
                  transition={{
                    duration: 2,
                    ease: "easeInOut",
                    delay: 0.3
                  }}
                  style={{ 
                    transformOrigin: "center",
                    transform: "rotate(-90deg)"
                  }}
                />
              ) : (
                // For desktop: Animate based on inView
                <motion.circle 
                  cx="50" 
                  cy="50" 
                  r="45" 
                  fill="none" 
                  stroke="#AE02FD" 
                  strokeWidth="8" 
                  strokeLinecap="round"
                  strokeDasharray="283"
                  strokeDashoffset="283"
                  initial={{ strokeDashoffset: 283 }}
                  animate={progressInView ? 
                    { strokeDashoffset: 283 - (283 * 0.9) } : 
                    { strokeDashoffset: 283 }
                  }
                  transition={{
                    duration: 2,
                    ease: "easeInOut",
                    delay: 0.3
                  }}
                  style={{ 
                    transformOrigin: "center",
                    transform: "rotate(-90deg)"
                  }}
                />
              )}
            </svg>
            
            <div className="absolute inset-0 flex items-center justify-center">
              {isMobile ? (
                // For mobile: Always animate to full value
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-3xl font-bold text-primary-purple-400"
                >
                  <CountUp 
                    key={`mobile-count-${activeIndex}`} // Re-trigger on slide change
                    start={0}
                    end={90}
                    duration={2}
                    delay={0.3}
                    separator=""
                    decimals={0}
                    suffix="%"
                  />
                </motion.div>
              ) : (
                // For desktop: Animate based on inView
                <motion.div
                  className="text-3xl font-bold text-primary-purple-400"
                >
                  <CountUp 
                    start={0}
                    end={progressInView ? 90 : 0}
                    duration={2}
                    delay={0.3}
                    separator=""
                    decimals={0}
                    suffix="%"
                  />
                </motion.div>
              )}
            </div>
          </div>
          
          {/* Progress bars - separate for mobile and desktop */}
          <div className="w-full space-y-4 mt-4 md:mt-0">
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-gray-700">Weekly Progress</span>
                <span className="font-medium text-primary-purple-400">85%</span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                {isMobile ? (
                  <motion.div 
                    className="h-full bg-primary-purple-200"
                    initial={{ width: 0 }}
                    animate={{ width: '85%' }}
                    transition={{ duration: 1.5, delay: 0.2 }}
                  />
                ) : (
                  <motion.div 
                    className="h-full bg-primary-purple-200"
                    initial={{ width: '0%' }}
                    animate={progressInView ? { width: '85%' } : { width: '0%' }}
                    transition={{ duration: 1.5, delay: 0.2 }}
                  />
                )}
              </div>
            </div>
            
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-gray-700">Skill Mastery</span>
                <span className="font-medium text-primary-green-50">70%</span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                {isMobile ? (
                  <motion.div 
                    className="h-full bg-primary-green-50"
                    initial={{ width: 0 }}
                    animate={{ width: '70%' }}
                    transition={{ duration: 1.5, delay: 0.4 }}
                  />
                ) : (
                  <motion.div 
                    className="h-full bg-primary-green-50"
                    initial={{ width: '0%' }}
                    animate={progressInView ? { width: '70%' } : { width: '0%' }}
                    transition={{ duration: 1.5, delay: 0.4 }}
                  />
                )}
              </div>
            </div>
            
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-gray-700">Understanding</span>
                <span className="font-medium text-primary-blue-50">95%</span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                {isMobile ? (
                  <motion.div 
                    className="h-full bg-primary-blue-50"
                    initial={{ width: 0 }}
                    animate={{ width: '95%' }}
                    transition={{ duration: 1.5, delay: 0.6 }}
                  />
                ) : (
                  <motion.div 
                    className="h-full bg-primary-blue-50"
                    initial={{ width: '0%' }}
                    animate={progressInView ? { width: '95%' } : { width: '0%' }}
                    transition={{ duration: 1.5, delay: 0.6 }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      ref: notificationRef,
      inView: notificationInView,
      title: "Notifications on key achievements and areas needing support",
      description: "Stay informed about your child's learning journey with timely alerts",
      renderContent: (isMobile) => (
        <div className='w-full'>
          <div className="relative w-full h-32 mb-6 flex items-center justify-center">
            {/* Bell icon */}
            <Icon icon="mdi:bell-outline" className="w-16 h-16 text-gray-700" />
            
            {/* Rotating notification dot */}
            <motion.div
              className="absolute w-4 h-4 bg-primary-purple-400 rounded-full"
              initial={{ rotate: 0, x: 0, y: -30 }}
              animate={isMobile || notificationInView ? 
                { 
                  rotate: 360,
                  x: [0, 15, 0, -15, 0],
                  y: [-30, -15, 0, -15, -30]
                } : 
                { rotate: 0, x: 0, y: -30 }
              }
              transition={{
                duration: 3,
                ease: "linear",
                repeat: (isMobile || notificationInView) ? Infinity : 0,
                repeatType: "loop"
              }}
            />
          </div>
          
          {/* Notification items */}
          <div className="w-full space-y-4 mb-4">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-gray-200 rounded-full mr-3"></div>
              <div className="h-2 bg-gray-200 rounded-full flex-1"></div>
            </div>
            
            <div className="flex items-center">
              <div className="w-4 h-4 bg-gray-300 rounded-full mr-3"></div>
              <div className="h-2 bg-gray-300 rounded-full flex-1"></div>
            </div>
            
            <div className="flex items-center">
              <div className="w-4 h-4 bg-gray-200 rounded-full mr-3"></div>
              <div className="h-2 bg-gray-200 rounded-full flex-1"></div>
            </div>
          </div>
        </div>
      )
    },
    {
      ref: dataRef,
      inView: dataInView,
      title: "Data-driven recommendations for improvement",
      description: "Get personalized strategies to help your child excel in areas that need attention",
      renderContent: (isMobile) => (
        <div className='w-full'>
          <div className="relative w-full h-32 mb-6 flex items-center justify-center">
            {/* Central node */}
            <div className="w-14 h-14 bg-primary-blue-50 rounded-full flex items-center justify-center z-10">
              <Icon icon="carbon:data-vis-4" className="w-8 h-8 text-white" />
            </div>
            
            {/* Orbiting data points */}
            {[1, 2, 3, 4].map((i) => (
              <motion.div
                key={i}
                className={`absolute w-5 h-5 rounded-full ${i % 2 === 0 ? 'bg-teal-400' : 'bg-purple-500'} flex items-center justify-center`}
                initial={{ 
                  x: 0, 
                  y: 0,
                  opacity: 0
                }}
                animate={isMobile || dataInView ? {
                  x: [0, 30 * Math.cos(i * Math.PI/2), 0],
                  y: [0, 30 * Math.sin(i * Math.PI/2), 0],
                  opacity: 1
                } : {
                  x: 0,
                  y: 0,
                  opacity: 0
                }}
                transition={{
                  duration: 3,
                  ease: "easeInOut",
                  repeat: (isMobile || dataInView) ? Infinity : 0,
                  repeatType: "reverse",
                  delay: i * 0.2
                }}
              >
                <Icon icon="mdi:chart-bubble" className="w-3 h-3 text-white" />
              </motion.div>
            ))}
          </div>
          
          {/* Recommendation items */}
          <div className="w-full space-y-4 mb-4 ">
            <div className="flex items-center justify-between">
              <div className="h-2 bg-teal-100 rounded-full w-[70%]"></div>
              <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center">
                <Icon icon="mdi:arrow-up" className="w-4 h-4 text-teal-500" />
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="h-2 bg-purple-100 rounded-full w-[60%]"></div>
              <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                <Icon icon="mdi:arrow-up" className="w-4 h-4 text-purple-500" />
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="h-2 bg-blue-100 rounded-full w-[85%]"></div>
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                <Icon icon="mdi:chart-line" className="w-4 h-4 text-blue-500" />
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];
  
  return (
    <section className="py-0 px-4 ">
      <div className="max-w-screen-xl mx-auto">
        {/* Mobile Carousel View */}
        <div className="block md:hidden">
          <Swiper
            modules={[Pagination]}
            pagination={{ clickable: true }}
            spaceBetween={30}
            slidesPerView={1}
            className="!pb-12" // Add space for pagination dots
            onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
            onSwiper={(swiper) => {
              // Force initial animation when swiper loads
              setTimeout(() => {
                setActiveIndex(swiper.activeIndex);
              }, 100);
            }}
          >
            {cards.map((card, index) => (
              <SwiperSlide key={index}>
                <motion.div 
                  className="bg-white p-4 rounded-xl shadow-md flex flex-col items-center text-center min-h-[500px]"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  key={`slide-${activeIndex === index ? 'active' : 'inactive'}-${index}`}
                >
                  {card.renderContent(true)}
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {card.title}
                  </h3>
                  <p className="text-gray-600">
                    {card.description}
                  </p>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        
        {/* Desktop Grid View */}
        <div className="hidden md:grid md:grid-cols-3 gap-8">
          {cards.map((card, index) => (
            <motion.div 
              key={index}
              ref={card.ref}
              className="bg-white p-8 rounded-xl shadow-md flex flex-col items-center text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={card.inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
            >
              {card.renderContent(false)}
              
              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-auto">
                {card.title}
              </h3>
              <p className="text-gray-600 ">
                {card.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AnimatedFeatureCards;
